import React from 'react';
import { Tooltip, Box, Theme, useTheme } from '@mui/material';
import { GridToolbarContainer, GridToolbarQuickFilter, GridToolbarExport } from '@mui/x-data-grid';
import moment from 'moment';
import { ReactComponent as ResponseIcon } from '../../../assets/icons/Response.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/Search.svg';
import { ReactComponent as ExportIcon } from '../../../assets/icons/Download.svg';
import { QualitativeMetricRecordGridRow } from './QualitativeMetricRecordGrid';
import { CompanyContext } from '../../../context/CompanyContext';
import { MinorHeading } from '../../shared/typography/PrimaryTypography';
import { MetricRecordCaptureParameters } from '../../../views/metric_collection/MetricCollectionDataManagement';

interface QualitativeMetricRecordGridToolbarProps {
  metric_record_rows: Array<QualitativeMetricRecordGridRow>;
  capture_parameters: MetricRecordCaptureParameters;
}

/**
 * Datagrid toolbar for creating new metric records manually for given parameters.
 * @param {Array<QualitativeMetricRecordGridRow>} metric_record_rows Existing metric records to parse for completeness.
 * @param {MetricRecordCaptureParameters} capture_parameters Parameters of current metric record capturing flow from previous inputs.
 * @returns {JSX.Element}
 */
export const QualitativeMetricRecordGridToolbar = ({
  metric_record_rows,
  capture_parameters
}: QualitativeMetricRecordGridToolbarProps) => {
  const { company } = React.useContext(CompanyContext);
  const theme: Theme = useTheme();
  const completed_responses: number = metric_record_rows.filter(
    (row: QualitativeMetricRecordGridRow) => {
      return row.value;
    }
  ).length;
  return (
    <>
      <Box sx={{ px: 2, py: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
        <span
          style={{
            color:
              completed_responses === metric_record_rows.length
                ? theme.palette.primary.main
                : theme.palette.warning.main,
            display: 'contents'
          }}
        >
          <MinorHeading
            weight="Medium"
            sx={{ color: 'inherit' }}
          >{`${completed_responses}/${metric_record_rows.length} Completed Responses`}</MinorHeading>
          <ResponseIcon height={30} stroke="currentColor" fill="currentColor" strokeWidth={2} />
        </span>
      </Box>
      <GridToolbarContainer sx={{ px: 2, mb: 2 }}>
        <GridToolbarQuickFilter
          slotProps={{
            input: {
              startAdornment: <SearchIcon height={15} />
            }
          }}
          style={{ flex: 1 }}
        />
        <Tooltip title="Export Metric Records to device">
          <GridToolbarExport
            printOptions={{ disableToolbarButton: true }}
            startIcon={<ExportIcon height={25} />}
            csvOptions={{
              fileName:
                company?.name && capture_parameters.reporting_period_group
                  ? `${company.name}_${capture_parameters.reporting_period_group.name}_qualitative_export_${moment().format('YYYYMMDD')}`
                  : `qualitative_export_${moment().format('YYYYMMDD')}`
            }}
          />
        </Tooltip>
      </GridToolbarContainer>
    </>
  );
};
